import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismInput,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import type { TaskForAsyncTaskFunction } from 'ember-concurrency';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import GeneralNavigationIntercomHelpLink from 'lumen/components/general/navigation/intercom-help-link';
import {
  SETUP_STEP_TYPES,
  type SetupStepType,
} from 'lumen/utils/constants/otb-selfservice-constants';

type Answer = string | boolean | number | null;

type BooleanOption = {
  label: string;
  value: boolean;
};

interface SettingsMyotbStepItemSignature {
  Args: {
    answer: Answer;
    helpText?: string;
    helpUrl?: string;
    name: string;
    order?: number;
    setAnswer: TaskForAsyncTaskFunction<
      { enqueue: boolean },
      (setupStepName: string, setupStepAnswer: Answer) => Promise<void>
    >;
    stepType: SetupStepType;
  };
}

export default class SettingsMyotbStepItem extends Component<SettingsMyotbStepItemSignature> {
  @service declare intl: Services['intl'];

  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked value = this.args.answer;

  booleanOptions: BooleanOption[];

  get isBooleanTypeStep(): boolean {
    return this.args.stepType === SETUP_STEP_TYPES.BOOLEAN;
  }

  get isCheckboxDisabled(): boolean {
    if (!this.isInputStep) {
      return false;
    }

    const { value } = this;

    return value === null || value === undefined || value === '';
  }

  get isCompleted(): boolean {
    return this.args.answer !== null && this.args.answer !== undefined;
  }

  get isInputStep(): boolean {
    return (
      this.isNumberTypeStep || this.isTextTypeStep || this.isBooleanTypeStep
    );
  }

  get isNumberTypeStep(): boolean {
    return this.args.stepType === SETUP_STEP_TYPES.INTEGER;
  }

  get isTextTypeStep(): boolean {
    return this.args.stepType === SETUP_STEP_TYPES.STRING;
  }

  get numberOrStringAnswer(): string | number {
    return this.args.answer as string | number;
  }

  get parsedValue(): Answer {
    if (this.isNumberTypeStep) {
      return parseInt(this.value as string, 10);
    }

    return this.value;
  }

  get selectedBooleanOption(): BooleanOption | undefined {
    return this.booleanOptions.find(
      (booleanOption) => booleanOption.value === this.args.answer,
    );
  }

  constructor(owner: Owner, args: SettingsMyotbStepItemSignature['Args']) {
    super(owner, args);

    this.booleanOptions = [
      {
        label: this.intl.t('general.yes'),
        value: true,
      },
      {
        label: this.intl.t('general.no'),
        value: false,
      },
    ];
  }

  @action onChange(option: BooleanOption): void {
    // When selecting an option, the step should be completed (AKA answer should be saved).
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.args.setAnswer.perform(this.args.name, option.value);
  }

  @action onFocusOut(): void {
    if (this.isCheckboxDisabled) {
      return;
    }

    // When the input field loses focus, the step should be completed (AKA answer should be saved).
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.args.setAnswer.perform(this.args.name, this.parsedValue);
  }

  @action toggleCompleted(): void {
    if (this.isCompleted) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.args.setAnswer.perform(this.args.name, null);
      return;
    }

    const answer = this.isInputStep ? this.parsedValue : true;

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.args.setAnswer.perform(this.args.name, answer);
  }

  @action updateValue(event: Event): void {
    this.value = (event.target as HTMLInputElement).value;
  }

  <template>
    <div
      class="rounded-200 border-neutral-default flex items-center justify-between gap-300 border border-solid p-300"
    >
      <div class="flex gap-200">
        {{#if @order}}
          <span class="text-300--bold text-nowrap">
            {{t "general.step" count=@order}}
          </span>
        {{/if}}

        <div class="flex flex-col gap-100">
          <span class="text-300--regular">{{t @name}}</span>

          {{#if this.isInputStep}}
            <div class="step-item__input flex flex-col">
              {{#if (or this.isNumberTypeStep this.isTextTypeStep)}}
                <PrismInput
                  @disabled={{this.isCompleted}}
                  @onFocusOut={{this.onFocusOut}}
                  @onInput={{this.updateValue}}
                  @removePadding={{true}}
                  @type={{if this.isNumberTypeStep "number" "text"}}
                  @value={{this.numberOrStringAnswer}}
                />
              {{else if this.isBooleanTypeStep}}
                {{#if this.isCompleted}}
                  <p class="text-300--medium">
                    {{this.selectedBooleanOption.label}}
                  </p>
                {{else}}
                  <PrismSelectInput
                    @onChange={{this.onChange}}
                    @options={{this.booleanOptions}}
                    @value={{this.selectedBooleanOption}}
                  />
                {{/if}}
              {{/if}}
            </div>
          {{/if}}

          {{#if @helpText}}
            {{#if @helpUrl}}
              <GeneralNavigationIntercomHelpLink
                @articleLink={{@helpUrl}}
                @iconName="link-out"
                @isStandaloneLink={{true}}
                @linkText={{t @helpText}}
                @type="text-and-icon"
                {{! OTB-TODO: cut off url because API passes full url }}
              />
            {{/if}}
          {{/if}}
        </div>
      </div>

      <PrismButton
        @buttonType={{if this.isCompleted "success" "secondary"}}
        @iconBefore={{if this.isCompleted "checkmark"}}
        @isDisabled={{this.isCheckboxDisabled}}
        @label={{if
          this.isCompleted
          (t "general.done")
          (t "general.mark_as_done")
        }}
        @onClick={{this.toggleCompleted}}
        @size="small"
      />
    </div>
  </template>
}
